import { createSlice } from '@reduxjs/toolkit'
import APILoadingStatus from 'types/api-loading-status'
import asyncReducers from '../../../utils/async-reducers'
import toast from 'react-hot-toast'
import {
  cancelContractCourt,
  createContractCourt,
  getContractCourt,
  listContractCourts,
  previewCancellation,
  previewShortening,
  updateEndDate,
} from './contractCourts.actions'
import { ContractCourt } from 'models/contract-court.model'

const getErrorMessage = (error: string | boolean): string =>
  typeof error === 'string' ? error : 'An error occurred'

export type ContractCourtsState = {
  listStatus: APILoadingStatus
  currentStatus: APILoadingStatus
  contracts: ContractCourt[] | null
  total: number
  currentContract: ContractCourt | null
}

const initialState: ContractCourtsState = {
  listStatus: APILoadingStatus.Idle,
  currentStatus: APILoadingStatus.Idle,
  contracts: null,
  total: 0,
  currentContract: null,
}

const contractCourtsSlice = createSlice({
  name: 'contractCourts',
  initialState,
  reducers: {
    clearCurrentContract(state) {
      state.currentContract = null
    },
  },
  extraReducers: builder => {
    asyncReducers(builder, listContractCourts, 'listStatus', (state, action) => {
      if (action.payload.error) {
        toast.error(getErrorMessage(action.payload.error))
        // Don't leave a stale total behind a failed refresh — the pagination
        // control would otherwise show pages for rows we no longer have.
        state.total = 0
        return
      }
      // product#540: the DEFAULT (non-paginated) call returns a FLAT ARRAY of
      // contracts; pagination is opt-in and ONLY then nests into the envelope
      // { contracts, summary.total_contracts } (mirrors list_campaign_stats).
      // Shape-detect so both the default list and the paginated page work.
      const data = action.payload.data
      if (Array.isArray(data)) {
        state.contracts = data
        state.total = data.length
      } else {
        state.contracts = data?.contracts ?? []
        state.total = data?.summary?.total_contracts ?? 0
      }
    })
    asyncReducers(builder, getContractCourt, 'currentStatus', (state, action) => {
      if (action.payload.error) {
        toast.error(getErrorMessage(action.payload.error))
        return
      }
      state.currentContract = action.payload.data
    })
    asyncReducers(builder, createContractCourt, 'currentStatus', (state, action) => {
      if (action.payload.error) {
        toast.error(getErrorMessage(action.payload.error))
        return
      }
      toast.success('Contract court created.')
    })
    asyncReducers(builder, cancelContractCourt, 'currentStatus', (state, action) => {
      if (action.payload.error) {
        toast.error(getErrorMessage(action.payload.error))
        return
      }
      toast.success('Contract court canceled.')
    })
    // Preview thunks don't mutate state; the modals consume the payload
    // directly via the createAsyncThunk return value. No fulfilled handler is
    // passed (vs an empty `() => {}` which ESLint's no-empty-function rule
    // blocks during the Heroku CI build). updateEndDate does refresh
    // currentContract since the contract row changed.
    asyncReducers(builder, previewCancellation, 'currentStatus')
    asyncReducers(builder, previewShortening, 'currentStatus')
    asyncReducers(builder, updateEndDate, 'currentStatus', (state, action) => {
      if (action.payload.error) {
        toast.error(getErrorMessage(action.payload.error))
        return
      }
      state.currentContract = action.payload.data
      toast.success('Contract end date updated.')
    })
  },
})

export default contractCourtsSlice.reducer
export const contractCourtsActions = {
  ...contractCourtsSlice.actions,
  listContractCourts,
  getContractCourt,
  createContractCourt,
  cancelContractCourt,
  previewCancellation,
  previewShortening,
  updateEndDate,
}
